import currencyToInt from '@/helpers/currencyToInt'
import isSpacedString from '@/helpers/isSpacedString'
import Fuse from 'fuse.js'
import moment from 'moment'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { OrderConditions } from '@/enums/orderConditions'

import { Coordinate } from '@/interfaces/coordinate'
import { PolicyInformation } from '@/interfaces/policyInformation'

import { displayedCategory } from '@/modules/hotels/composables/useHotelCategory'
import {
	type CancellationFilter,
	areaKey,
	areaOptions,
	cancellationCounts,
	chainKey,
	chainOptions,
	cityKey,
	cityOptions,
	matchesCancellation,
} from '@/modules/hotels/helpers/hotelFilters'
import { Hotel } from '@/modules/hotels/interfaces/hotel'
import { HotelPreferencesSummary } from '@/modules/hotels/interfaces/hotelListResponse'
import type { HotelSearchContext } from '@/modules/hotels/interfaces/hotelSearchSettings'
import { HotelsSearchRequest } from '@/modules/hotels/interfaces/hotelsSearchRequest'

const fuseOptions = {
	includeScore: true,
	keys: ['name'],
	shouldSort: true,
	threshold: 0.48,
	findAllMatches: true,
}

const searchWithFuse = (
	items: Hotel[],
	query: string,
	fuseInstance: Fuse<Hotel> | null
): Hotel[] => {
	if (!query || isSpacedString(query)) return items
	const fuse = fuseInstance || new Fuse(items, fuseOptions)
	return fuse.search(query).map((result) => result.item)
}

const defaults = {
	form: {
		Start: '',
		End: '',
		HotelCityCode: undefined,
		Quantity: 1,
		GuestCount: 1,
		IsPerRoom: true,
		Rating: 0,
		latitude: 0,
		longitude: 0,
		users: [],
		memberId: '',
	},
	center: {
		lat: 0,
		lng: 0,
	},
}

export const useHotelsStore = defineStore('hotels', () => {
	const form = ref<HotelsSearchRequest>({ ...defaults.form })
	const policyInformation = ref<PolicyInformation>()
	const hotels = ref<Hotel[]>([])
	const selectedUsers = ref<number[]>([])
	const selectedHotel = ref<Hotel | undefined>(undefined)
	const center = ref<Coordinate>({ ...defaults.center })
	const zoom = ref<number>(0)
	const searchQuery = ref<string>('')
	const orderBy = ref<string>(OrderConditions.AscendingSort)
	const favoritePriceOrder = ref<'asc' | 'desc'>('asc')
	const moreIndicator = ref<string>('')
	// ¿Se le muestra al cliente de qué proveedor viene cada tarifa? Lo decide la agencia
	// desde el panel del monolito. Default OCULTO: si la respuesta no trae la llave (p. ej.
	// búsqueda que es passthrough puro a Amadeus), no se expone nuestra cadena de proveedores.
	const showProviderBadge = ref<boolean>(false)
	// Fila del historial (`public.hotel_searches`) de la búsqueda en pantalla. Viaja por
	// la URL al detalle y a la tarifa para que /pnrs/create la marque «Con reserva».
	const searchId = ref<number | null>(null)
	// Qué hizo el backend con los hoteles preferidos de la empresa en la búsqueda en
	// pantalla. null = la empresa no tiene lista y la respuesta salió como siempre.
	const hotelPreferences = ref<HotelPreferencesSummary | null>(null)
	const searchContext = ref<HotelSearchContext | null>(null)
	const errorMessage = ref<any>()

	// Bandera explícita para bloquear/mostrar loading en la lista mientras
	// corre una búsqueda disparada desde la barra rápida (botón Actualizar).
	// No depende de que isFetching de vue-query se sincronice entre
	// instancias separadas de useHotels() (AvailabilitySummarySearch vs esta
	// página) — ese supuesto ya falló una vez, así que se controla a mano.
	const isQuickSearching = ref<boolean>(false)

	// Filtros client-side sobre los resultados ya cargados (hotels.value).
	// No disparan una nueva búsqueda al backend, a diferencia de form.Rating/
	// minRate/maxRate (esos son parámetros de la búsqueda inicial, sin UI hoy).
	const minRatingFilter = ref<number>(0)
	const minPriceFilter = ref<number | null>(null)
	const maxPriceFilter = ref<number | null>(null)
	const accommodationTypeFilter = ref<string>('all')
	const boardFilter = ref<string>('all')
	const chainFilter = ref<string[]>([])
	const cityFilter = ref<string[]>([])
	const areaFilter = ref<string[]>([])
	const cancellationFilter = ref<CancellationFilter>('all')

	/**
	 * ¿El hotel pasa los filtros de resultados? `except` deja fuera una sección: sus
	 * opciones y conteos se calculan sobre lo que pasa TODO LO DEMÁS (como Expedia),
	 * así elegir una cadena acota Municipio, Zona y Cancelación sin vaciarse a sí misma.
	 */
	type ResultFacet =
		| 'rating'
		| 'price'
		| 'type'
		| 'board'
		| 'chain'
		| 'city'
		| 'area'
		| 'cancellation'

	const matchesResultFilters = (
		hotel: Hotel,
		except?: ResultFacet
	): boolean => {
		if (
			except !== 'rating' &&
			minRatingFilter.value > 0 &&
			displayedCategory(hotel).stars < minRatingFilter.value
		) {
			return false
		}

		if (except !== 'price') {
			const dailyRate = currencyToInt(hotel.dailyRateApproximate || '0')
			if (minPriceFilter.value !== null && dailyRate < minPriceFilter.value) {
				return false
			}
			if (maxPriceFilter.value !== null && dailyRate > maxPriceFilter.value) {
				return false
			}
		}

		if (
			except !== 'type' &&
			accommodationTypeFilter.value !== 'all' &&
			hotel.accommodationTypeName !== accommodationTypeFilter.value
		) {
			return false
		}

		if (
			except !== 'board' &&
			boardFilter.value !== 'all' &&
			hotel.board !== boardFilter.value
		) {
			return false
		}

		if (except !== 'chain' && chainFilter.value.length > 0) {
			const key = chainKey(hotel)
			if (!key || !chainFilter.value.includes(key)) return false
		}

		if (except !== 'city' && cityFilter.value.length > 0) {
			const key = cityKey(hotel)
			if (!key || !cityFilter.value.includes(key)) return false
		}

		if (except !== 'area' && areaFilter.value.length > 0) {
			const key = areaKey(hotel)
			if (!key || !areaFilter.value.includes(key)) return false
		}

		if (
			except !== 'cancellation' &&
			!matchesCancellation(hotel, cancellationFilter.value)
		) {
			return false
		}

		return true
	}

	const hotelsMatchingExcept = (facet: ResultFacet) =>
		hotels.value.filter((hotel) => matchesResultFilters(hotel, facet))

	/** Valores distintos de un campo entre los hoteles que pasan los demás filtros; el elegido siempre queda. */
	const facetValues = (
		facet: ResultFacet,
		valueOf: (hotel: Hotel) => string | null | undefined,
		selected: string
	) => {
		const values = new Set(
			hotelsMatchingExcept(facet)
				.map(valueOf)
				.filter((value): value is string => !!value)
		)
		if (selected !== 'all') values.add(selected)
		return Array.from(values).sort()
	}

	// Instancia memoizada de Fuse - solo se recrea cuando cambian los hoteles
	const fuseInstance = ref<Fuse<Hotel> | null>(null)
	watch(
		hotels,
		(newHotels) => {
			fuseInstance.value =
				newHotels.length > 0 ? new Fuse(newHotels, fuseOptions) : null
		},
		{ deep: false }
	)

	return {
		form,
		hotels,
		selectedUsers,
		selectedHotel,
		center,
		zoom,
		searchQuery,
		orderBy,
		favoritePriceOrder,
		isQuickSearching,
		minRatingFilter,
		minPriceFilter,
		maxPriceFilter,
		accommodationTypeFilter,
		boardFilter,
		chainFilter,
		cityFilter,
		areaFilter,
		cancellationFilter,
		moreIndicator,
		showProviderBadge,
		searchId,
		hotelPreferences,
		searchContext,
		policyInformation,
		errorMessage,

		policyErrorFlag: computed(() => {
			const { errors, warnings } = policyInformation.value || {}
			return !!(errors?.length || warnings?.length || false)
		}),
		// Opciones dinámicas: solo lo que realmente aparece en los resultados
		// cargados (mismo criterio que el combobox de bugReporters en booking-internal).
		accommodationTypeOptions: computed(() =>
			facetValues(
				'type',
				(hotel) => hotel.accommodationTypeName,
				accommodationTypeFilter.value
			)
		),
		boardOptions: computed(() =>
			facetValues('board', (hotel) => hotel.board, boardFilter.value)
		),
		chainOptions: computed(() =>
			chainOptions(
				hotels.value,
				hotelsMatchingExcept('chain'),
				chainFilter.value
			)
		),
		cityOptions: computed(() =>
			cityOptions(hotels.value, hotelsMatchingExcept('city'), cityFilter.value)
		),
		areaOptions: computed(() =>
			areaOptions(hotels.value, hotelsMatchingExcept('area'), areaFilter.value)
		),
		cancellationCounts: computed(() =>
			cancellationCounts(hotelsMatchingExcept('cancellation'))
		),
		activeFilterCount: computed(() => {
			let count = 0
			if (minRatingFilter.value > 0) count++
			if (minPriceFilter.value !== null) count++
			if (maxPriceFilter.value !== null) count++
			if (accommodationTypeFilter.value !== 'all') count++
			if (boardFilter.value !== 'all') count++
			count += chainFilter.value.length
			count += cityFilter.value.length
			count += areaFilter.value.length
			if (cancellationFilter.value !== 'all') count++
			return count
		}),

		filteredHotelList: computed(() => {
			const result = hotels.value.filter((hotel) => matchesResultFilters(hotel))
			// La política corporativa prevalece sobre convenios, favoritos y precio,
			// incluso después de una búsqueda por nombre con Fuse.
			const corporateOrder = (items: Hotel[]) =>
				[...items].sort(
					(a, b) => Number(!!b.companyPreferred) - Number(!!a.companyPreferred)
				)

			let resultsNoAgreementsHotels = [
				...result.filter((hotel) => {
					return !hotel.agreement && !hotel.companyName
				}),
			]

			let resultsAgreementsHotels = [
				...result.filter((hotel) => {
					if (hotel.agreement || Boolean(hotel.companyName)) {
						return hotel
					}
				}),
			]

			const sortingByOrderBy = (a: Hotel, b: Hotel) => {
				switch (orderBy.value) {
					case OrderConditions.AscendingSort:
						return (
							currencyToInt(a.dailyRateApproximate) -
							currencyToInt(b.dailyRateApproximate)
						)
					case OrderConditions.DescendingSort:
						return (
							currencyToInt(b.dailyRateApproximate) -
							currencyToInt(a.dailyRateApproximate)
						)
					case OrderConditions.Favorites:
						return sortingBySomeFavorite(a, b)
					default:
						return 0
				}
			}

			const sortingBySomeFavorite = (a: Hotel, b: Hotel) => {
				if (a.favorite && !b.favorite) {
					return -1
				}
				if (!a.favorite && b.favorite) {
					return 1
				}
				return 0
			}

			if (orderBy.value === OrderConditions.Favorites) {
				const hasAgreement = (hotel: Hotel) =>
					hotel.agreement || Boolean(hotel.companyName)

				const allHotels = [...result].sort((a: Hotel, b: Hotel) => {
					const favoriteSort = sortingBySomeFavorite(a, b)
					if (favoriteSort !== 0) return favoriteSort

					const aHasAgreement = hasAgreement(a)
					const bHasAgreement = hasAgreement(b)
					if (aHasAgreement && !bHasAgreement) return -1
					if (!aHasAgreement && bHasAgreement) return 1

					if (favoritePriceOrder.value === 'asc') {
						return (
							currencyToInt(a.dailyRateApproximate) -
							currencyToInt(b.dailyRateApproximate)
						)
					} else {
						return (
							currencyToInt(b.dailyRateApproximate) -
							currencyToInt(a.dailyRateApproximate)
						)
					}
				})

				return corporateOrder(
					searchWithFuse(allHotels, searchQuery.value, fuseInstance.value)
				)
			}

			resultsNoAgreementsHotels = resultsNoAgreementsHotels.sort(
				(a: Hotel, b: Hotel) => {
					return sortingByOrderBy(a, b)
				}
			)

			resultsAgreementsHotels = resultsAgreementsHotels.sort(
				(a: Hotel, b: Hotel) => {
					return sortingByOrderBy(a, b)
				}
			)

			const resultsConcat = [
				...resultsAgreementsHotels,
				...resultsNoAgreementsHotels,
			]

			return corporateOrder(
				searchWithFuse(resultsConcat, searchQuery.value, fuseInstance.value)
			)
		}),
		reservationNights: computed(() => {
			const { Start, End } = form.value

			return Number(Start && End ? moment(End).diff(moment(Start), 'days') : 0)
		}),
		setForm(value: HotelsSearchRequest) {
			form.value = value
		},
		/**
		 * Los dos setters cuidan lo mismo: `companyBranchId` sólo vale con el punto de la sede.
		 * Si cambian las coordenadas y la misma llamada no trae la sede, la búsqueda deja de
		 * ser de sede (punto en el mapa, cambio rápido de destino). Sin soltarla, BookingV2
		 * pondría encima el punto y el radio de la sede y filtraría con su lista: el
		 * círculo en un lado y los hoteles en otro.
		 */
		setFormValue<T extends keyof HotelsSearchRequest>(
			property: T,
			value: HotelsSearchRequest[T]
		) {
			form.value[property] = value

			if (property === 'latitude' || property === 'longitude') {
				form.value.companyBranchId = undefined
			}
		},
		setFormValues(value: Partial<HotelsSearchRequest>) {
			const movesPoint = 'latitude' in value || 'longitude' in value

			form.value = {
				...form.value,
				...(movesPoint && !('companyBranchId' in value) ?
					{ companyBranchId: undefined }
				:	{}),
				...value,
			}
		},
		setHotels(value: Hotel[]) {
			hotels.value = value
		},
		setMoreIndicator(value: string) {
			moreIndicator.value = value
		},
		setShowProviderBadge(value: boolean) {
			showProviderBadge.value = value
		},
		setSearchId(value: number | null) {
			searchId.value = value
		},
		setHotelPreferences(value: HotelPreferencesSummary | null) {
			hotelPreferences.value = value
		},
		setSearchContext(value: HotelSearchContext | null) {
			searchContext.value = value
		},
		setCenter(value: Coordinate) {
			center.value = value
		},
		setZoom(value: number) {
			zoom.value = value
		},
		setSearchQuery(value: string) {
			if (!isSpacedString(value)) {
				searchQuery.value = value
			}
		},
		setOrderBy(value: OrderConditions) {
			orderBy.value = value
		},
		setFavoritePriceOrder(value: 'asc' | 'desc') {
			favoritePriceOrder.value = value
		},
		setIsQuickSearching(value: boolean) {
			isQuickSearching.value = value
		},
		setMinRatingFilter(value: number) {
			minRatingFilter.value = value
		},
		setMinPriceFilter(value: number | null) {
			minPriceFilter.value = value
		},
		setMaxPriceFilter(value: number | null) {
			maxPriceFilter.value = value
		},
		setAccommodationTypeFilter(value: string) {
			accommodationTypeFilter.value = value
		},
		setBoardFilter(value: string) {
			boardFilter.value = value
		},
		resetResultFilters() {
			minRatingFilter.value = 0
			minPriceFilter.value = null
			maxPriceFilter.value = null
			accommodationTypeFilter.value = 'all'
			boardFilter.value = 'all'
			chainFilter.value = []
			cityFilter.value = []
			areaFilter.value = []
			cancellationFilter.value = 'all'
		},
		setSelectedHotel(value: Hotel | undefined) {
			selectedHotel.value = value
		},
		setSelectedUsers(value: number[]) {
			selectedUsers.value = value
		},
		setPolicyInformation(value: PolicyInformation) {
			policyInformation.value = value
		},
		resetFilters() {
			form.value.Rating = undefined
			form.value.maxRate = undefined
			form.value.minRate = undefined
			selectedHotel.value = undefined
		},
		resetValues() {
			searchContext.value = null
			hotelPreferences.value = null
			searchQuery.value = ''
			searchId.value = null
			orderBy.value = OrderConditions.AscendingSort
			form.value = defaults.form
			center.value = defaults.center
			hotels.value = []
			selectedHotel.value = undefined
			minRatingFilter.value = 0
			minPriceFilter.value = null
			maxPriceFilter.value = null
			accommodationTypeFilter.value = 'all'
			boardFilter.value = 'all'
			chainFilter.value = []
			cityFilter.value = []
			areaFilter.value = []
			cancellationFilter.value = 'all'
		},
		resetPolicyInformation() {
			policyInformation.value = {
				errors: undefined,
				warnings: undefined,
			}
		},
		setFavoriteHotel(id: string, favorite: boolean) {
			hotels.value = hotels.value.map((h) => {
				if (h.id === id) {
					return {
						...h,
						favorite,
					}
				}
				return h
			})
		},
		getHotel(id: string) {
			return hotels.value.find((h) => h.id === id)
		},
		setErrorMessage(value: string) {
			errorMessage.value = value
		},
	}
})

// Sin esto, al editar el store en dev la página conserva la instancia vieja (con sus
// computed viejos) hasta recargar a mano.
if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useHotelsStore, import.meta.hot))
}
